import * as React from 'react';
import type {
  Validations,
  NormalizedSms,
  SmsState,
  // FetchedSmsResponse,
  SmsApi,
} from 'src/types/sms';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import type {
  EntityRelationship,
  EntityRelationshipWithLevelAndParent,
} from 'src/types/ats-entities';
import {
  getStringWithoutAutomationIdentifiers,
  escapeVarNames,
} from 'src/utils/automation';

import {
  normalizeNodeSchedule,
  parseNodeSchedule,
} from 'src/utils/node-schedule';
import {FormLabelSmall} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  normalizeMessagingNoteWriteback,
  parseMessagingNoteWriteback,
} from 'src/utils/note-writeback';
import {normalizeNodeAlerts, parseNodeAlerts} from 'src/utils/node-alerts';
import type {
  TranslationFunction,
  MakeKeyFunction,
} from 'src/types/language-support';


export const SMS_CONTENT_SIZE = 720;

export const SMS_TRANSMISSION_LEARN_MORE_LINK =
  'https://support.sensehq.co/hc/en-us/articles/31332020693901-SMS-Transmission-and-Best-Practices';

export const smsKeywords = [
  'help',
  'start',
  'stop',
  'end',
  'cancel',
  'unsubscribe',
  'quit',
] as const;

export type SenderOption = {
  label: string;
  value: string;
  variableValue: string;
  relatedEntity: string;
};

export const getSendFromOptions = (
  t: TranslationFunction,
): Array<MenuOption> => [
  {label: t('Variable', 'Variable'), key: 'variable'},
  {
    label: t('Content_category_phone_number', 'Content category phone number'),
    key: 'content_category_phone_number',
  },
  {
    label: t(
      'Content_subscription_backup_number',
      'Content subscription backup number',
    ),
    key: 'content_subscription_backup_number',
  },
];

export const normalizeSms = (smsState: SmsState): NormalizedSms => {
  const {
    to_number,
    from_number,
    category_id,
    external_source_id,
    body,
    sender_category,
  } = smsState;
  const nodeSchedule = normalizeNodeSchedule(smsState);
  const noteWriteback = normalizeMessagingNoteWriteback(smsState);
  const nodeAlerts = normalizeNodeAlerts(smsState.alerts ?? []);
  return {
    task: {
      params: {
        to_number,
        from_number,
        external_source_id,
        category_id,
        body,
        sender_category,
      },
    },
    ...nodeSchedule,
    ...noteWriteback,
    ...nodeAlerts,
  };
};

export const getSenderListWithItsEntityVariables = (
  anchorEntityType: string,
  relatedInternalEntities: EntityRelationshipWithLevelAndParent[],
): Array<SenderOption> => {
  const getVariableValue = (
    entity: EntityRelationshipWithLevelAndParent,
    anchorEntityType: string,
  ) => {
    const relatedEntitiesPath =
      entity?.level === 2
        ? `.relatedEntities.${escapeVarNames(
            entity?.parent?.name ?? '',
          )}.relatedEntities.${escapeVarNames(entity.name)}`
        : `.relatedEntities.${escapeVarNames(entity.name)}`;
    return `{{$.entity_group_fetch__${anchorEntityType}.response${relatedEntitiesPath}.entity.externalSourceId}}`;
  };

  /** Menu / lookup key: L2 scopes by parent relation name (same graph as variableValue). */
  const getSenderOptionValue = (
    entity: EntityRelationshipWithLevelAndParent,
  ): string => {
    if (entity.level === 2 && entity.parent?.name) {
      return `<${entity.parent.name}/${entity.join_attribute}>`;
    }
    return `<${entity.join_attribute}>`;
  };

  const getRelatedEntityKeyForOptions = (
    entity: EntityRelationshipWithLevelAndParent,
  ): string => {
    if (entity.level === 2 && entity.parent?.name) {
      return `${entity.parent.name}/${entity.name}`;
    }
    return entity.name;
  };

  const senderVariables = anchorEntityType
    ? relatedInternalEntities.map((e) => ({
        label:
          e?.level === 2
            ? `${e?.parent?.display_name}/${e.display_name}`
            : e.display_name,
        value: getSenderOptionValue(e),
        variableValue: getVariableValue(e, anchorEntityType),
        relatedEntity: getRelatedEntityKeyForOptions(e),
      }))
    : [];
  return senderVariables;
};

export const getSenderNameFromVariable = (
  senderVariable: string,
  senderOptions: Array<SenderOption>,
): string => {
  const currentSender = senderOptions.find(
    (opt) => opt.variableValue === senderVariable,
  );
  return currentSender?.label ?? '';
};

export const getSenderNameFromRelatedEntityKey = (
  senderVariable: string,
  senderOptions: Array<SenderOption>,
): string => {
  const currentSender = senderOptions.find(
    (opt) => opt.variableValue === senderVariable,
  );
  return currentSender?.relatedEntity ?? '';
};

export const getErrors = (validationsObject: Validations): Array<string> => {
  const errors: Array<string> = [];
  const {
    senderNumberEmpty,
    sendToNumberEmpty,
    sameFromToNumber,
    invalidToNumber,
    nwbSentEntityNotSelected,
    nwbSentNoteActionNotSelected,
    nwbReplyEntityNotSelected,
    nwbReplyNoteActionNotSelected,
  } = validationsObject;
  if (senderNumberEmpty || sendToNumberEmpty) {
    errors.push('Please fill the required fields.');
  }
  if (sameFromToNumber) {
    errors.push('Sender Number cannot be same as Receiver Number.');
  }
  if (invalidToNumber) {
    errors.push('Send To Number is Invalid.');
  }
  if (nwbSentEntityNotSelected) {
    errors.push('Please select an entity for sent note writeback.');
  }
  if (nwbSentNoteActionNotSelected) {
    errors.push('Please select a sent note action.');
  }
  if (nwbReplyEntityNotSelected) {
    errors.push('Please select an entity for reply note writeback.');
  }
  if (nwbReplyNoteActionNotSelected) {
    errors.push('Please select a reply note action.');
  }
  return errors;
};

export const getContentErrors = (validationsObject: {
  contentBodyEmpty: boolean;
  contentLengthExceeded: boolean;
}): Array<string> => {
  const errors: Array<string> = [];
  if (validationsObject.contentBodyEmpty) {
    errors.push('Content body is required.');
  }
  if (validationsObject.contentLengthExceeded) {
    errors.push('SMS content is too long.');
  }
  return errors;
};

export const parseSms = (fetchedNodeResponse: SmsApi): SmsState => {
  const smsSendTask = fetchedNodeResponse.task;
  const taskDefinition = smsSendTask.params;
  if (!taskDefinition) {
    throw new Error('Sms task definition not found');
  }
  const nodeSchedule = parseNodeSchedule(fetchedNodeResponse);
  const noteWriteback = parseMessagingNoteWriteback(fetchedNodeResponse);
  const nodeAlerts = parseNodeAlerts(fetchedNodeResponse);
  const toNumber = String(taskDefinition.to_number);
  const surveysAttached = smsSendTask.surveys_attached ?? [];
  const meetingsAttached = smsSendTask.meetings_attached ?? [];

  return {
    body: taskDefinition.body,
    // @ts-expect-error - TS2345 - Argument of type 'number | null | undefined' is not assignable to parameter of type 'string'.
    category_id: parseInt(taskDefinition.category_id, 10),
    external_source_id: taskDefinition.external_source_id
      ? taskDefinition.external_source_id
      : null,
    from_number: taskDefinition.from_number ?? null,
    to_number:
      toNumber.startsWith('{{') && toNumber.endsWith('}}')
        ? toNumber
        : `{{${toNumber || ''}}}`,
    sender_category: taskDefinition.sender_category,
    surveysAttached,
    meetingsAttached,
    alerts: nodeAlerts.alerts ?? [],
    ...nodeSchedule,
    ...noteWriteback,
  };
};

export const isGreaterThanSmsContentSize = (value: string): boolean => {
  return getStringWithoutAutomationIdentifiers(value).length > SMS_CONTENT_SIZE;
};

export const getCombinedSenderOptions = ({
  anchorEntityType,
  triggerEntityType,
  relatedInternalEntities,
  triggerInternalEntities,
  standardEntityType,
}: {
  anchorEntityType: string;
  triggerEntityType: string;
  relatedInternalEntities: EntityRelationship[];
  triggerInternalEntities: EntityRelationship[];
  standardEntityType: string;
}): Array<SenderOption> => {
  const anchorSenderOptions = getSenderListWithItsEntityVariables(
    anchorEntityType || '',
    relatedInternalEntities,
  );

  const triggerSenderOptions =
    triggerEntityType !== anchorEntityType && standardEntityType === 'job'
      ? getSenderListWithItsEntityVariables(
          triggerEntityType || '',
          triggerInternalEntities,
        )
      : [];

  return [...anchorSenderOptions, ...triggerSenderOptions];
};

export const getSenderOptionsForDropdown = ({
  anchorEntityType,
  triggerEntityType,
  anchorEntityTypeDisplayName,
  relatedInternalEntities,
  triggerInternalEntities,
  standardEntityType,
  t,
  makeKey,
}: {
  anchorEntityType: string;
  triggerEntityType: string;
  relatedInternalEntities: EntityRelationship[];
  triggerInternalEntities: EntityRelationship[];
  standardEntityType: string;
  anchorEntityTypeDisplayName: string;
  t: TranslationFunction;
  makeKey: MakeKeyFunction;
}): Array<{
  groupTitle: React.ReactNode;
  options?: Array<MenuOption>;
  showLineDivider?: boolean;
}> => {
  const anchorSenderOptions = getSenderListWithItsEntityVariables(
    anchorEntityType || '',
    relatedInternalEntities,
  );

  const triggerSenderOptions =
    triggerEntityType !== anchorEntityType && standardEntityType === 'job'
      ? getSenderListWithItsEntityVariables(
          triggerEntityType || '',
          triggerInternalEntities,
        )
      : [];
  const anchorEntityTypeVariables = t(
    makeKey(`{{anchorEntityTypeDisplayName}} variables`),
    {anchorEntityTypeDisplayName, defaultValue: 'Candidate variables'},
  );
  return [
    {
      groupTitle: (
        <FormLabelSmall color="tertiary">
          {anchorEntityTypeVariables} {`(${anchorSenderOptions.length})`}
        </FormLabelSmall>
      ),
      options: anchorSenderOptions.map(({label, variableValue}) => ({
        key: variableValue,
        label,
      })),
      showLineDivider: true,
    },
    {
      groupTitle: (
        <FormLabelSmall color="tertiary">{`${t(
          'Joborder_variables',
          'Joborder variables',
        )} (${triggerSenderOptions.length})`}</FormLabelSmall>
      ),
      options: triggerSenderOptions.map(({label, variableValue}) => ({
        key: variableValue,
        label,
      })),
      showLineDivider: true,
    },
  ].filter((group) => (group.options?.length ?? 0) > 0);
};
